import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Cairo & felt252',
    tagline: 'A provable language built on field arithmetic, not bounded integers.',
};

// The Cairo / Starknet field prime: 2^251 + 17 * 2^192 + 1.
// Written as a decimal string so it compiles cleanly under the es5 target
// (BigInt numeric literals such as 2n require ES2020+).
const FIELD_PRIME = BigInt('3618502788666131213697322783095070105623107215331596699973092056135872020481');

const defaultA = (FIELD_PRIME - BigInt(5)).toString();
const defaultB = '12';

type FeltOp = 'add' | 'sub' | 'mul';

const opSymbol: Record<FeltOp, string> = { add: '+', sub: '-', mul: '*' };

const parseFelt = (raw: string): bigint | null => {
    const value = raw.trim();
    if (value === '') return null;
    try {
        const negative = value.startsWith('-');
        const body = negative ? value.slice(1) : value;
        const magnitude = BigInt(body);
        return negative ? -magnitude : magnitude;
    } catch (error) {
        return null;
    }
};

const modPrime = (value: bigint): bigint => ((value % FIELD_PRIME) + FIELD_PRIME) % FIELD_PRIME;

type FeltResult =
    | { ok: false; error: string }
    | { ok: true; fieldA: bigint; fieldB: bigint; fieldResult: bigint; wrapped: boolean };

const FeltArithmeticDemo = () => {
    const colors = useColors();
    const [rawA, setRawA] = useState(defaultA);
    const [rawB, setRawB] = useState(defaultB);
    const [op, setOp] = useState<FeltOp>('add');

    const result = useMemo<FeltResult>(() => {
        const a = parseFelt(rawA);
        const b = parseFelt(rawB);
        if (a === null || b === null) {
            return { ok: false, error: 'Enter two integers (decimal or negative).' };
        }

        const fieldA = modPrime(a);
        const fieldB = modPrime(b);
        let rawResult: bigint;
        if (op === 'add') rawResult = fieldA + fieldB;
        else if (op === 'sub') rawResult = fieldA - fieldB;
        else rawResult = fieldA * fieldB;

        const fieldResult = modPrime(rawResult);
        // "Wrapped" when the plain integer answer is out of [0, P) and the field
        // had to fold it back in: the exact assumption that breaks naive accounting.
        const wrapped = rawResult < BigInt(0) || rawResult >= FIELD_PRIME;

        return { ok: true, fieldA, fieldB, fieldResult, wrapped };
    }, [rawA, rawB, op]);

    const inputSx = {
        '& .MuiInputBase-input': {
            color: colors.text.primary,
            fontFamily: '"Share Tech Mono", monospace',
            fontSize: '0.82rem',
        },
        '& .MuiInputLabel-root': { color: colors.text.secondary },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default },
    };

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: '18px',
        }}>
            <Box sx={{
                p: '18px',
                borderRadius: '8px',
                backgroundColor: colors.bg.tertiary,
                border: `1px solid ${colors.border.subtle}`,
            }}>
                <Typography sx={{ color: colors.text.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                    Prime field order P
                </Typography>
                <Typography sx={{
                    color: colors.accent.purple,
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: '0.74rem',
                    lineHeight: 1.55,
                    wordBreak: 'break-all',
                    mb: 2.5,
                }}>
                    {FIELD_PRIME.toString()}
                </Typography>
                <TextField
                    label="Value a"
                    value={rawA}
                    onChange={(event) => setRawA(event.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 2, ...inputSx }}
                />
                <TextField
                    label="Value b"
                    value={rawB}
                    onChange={(event) => setRawB(event.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 2, ...inputSx }}
                />
                <ToggleButtonGroup
                    value={op}
                    exclusive
                    onChange={(_, value) => { if (value) setOp(value as FeltOp); }}
                    fullWidth
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            color: colors.text.secondary,
                            borderColor: colors.border.default,
                            fontFamily: '"Orbitron", sans-serif',
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                            color: colors.accent.purple,
                            backgroundColor: colors.accent.purpleGlow,
                        },
                    }}
                >
                    <ToggleButton value="add">a + b</ToggleButton>
                    <ToggleButton value="sub">a - b</ToggleButton>
                    <ToggleButton value="mul">a × b</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => { setRawA(defaultA); setRawB(defaultB); setOp('add'); }}
                        sx={{
                            borderColor: colors.border.default,
                            color: colors.text.secondary,
                            fontFamily: '"Orbitron", sans-serif',
                        }}
                    >
                        Reset to max felt + 12
                    </Button>
                </Box>
            </Box>

            <Box sx={{
                p: '18px',
                borderRadius: '8px',
                backgroundColor: colors.bg.tertiary,
                border: `1px solid ${!result.ok ? colors.border.subtle : result.wrapped ? colors.accent.orange : colors.accent.green}`,
            }}>
                {!result.ok ? (
                    <Typography sx={{ color: colors.text.secondary, fontFamily: '"Share Tech Mono", monospace' }}>
                        {result.error}
                    </Typography>
                ) : (
                    <>
                        <Typography sx={{ color: colors.text.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                            a {opSymbol[op]} b (mod P)
                        </Typography>
                        <Typography sx={{
                            color: result.wrapped ? colors.accent.orange : colors.accent.green,
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: { xs: '0.9rem', sm: '1.05rem' },
                            lineHeight: 1.5,
                            wordBreak: 'break-all',
                            mb: 2.5,
                            minHeight: '48px',
                        }}>
                            {result.fieldResult.toString()}
                        </Typography>
                        <Box sx={{
                            mt: 1,
                            p: '14px',
                            borderRadius: '8px',
                            backgroundColor: colors.bg.secondary,
                            border: `1px solid ${result.wrapped ? colors.accent.orange : colors.border.subtle}`,
                        }}>
                            <Typography sx={{ color: colors.text.primary, fontWeight: 800, mb: 0.5 }}>
                                {result.wrapped ? 'The field wrapped' : 'No wrap this time'}
                            </Typography>
                            <Typography sx={{ color: colors.text.secondary, lineHeight: 1.65, fontSize: '0.9rem' }}>
                                {result.wrapped
                                    ? 'The plain integer answer landed outside [0, P), so Cairo folded it back into the field. Code that assumed a normal overflow revert would be wrong here.'
                                    : 'The result already sits inside [0, P). Raise the inputs past the prime to see felt252 silently wrap with no error.'}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what felt252 arithmetic actually does at the boundary of the prime
//   Q1 - why Cairo is compiled to Sierra before CASM instead of straight to CASM
//   Q2 - the practical security consequence of felt252 not behaving like a bounded integer
export const blocks: StageBlock[] = [
    content('hero', () => (
        <StageSection gradientBorder eyebrow="Stage 2 of 8" title="Contracts are written in Cairo, a provable language">
            <Prose>
                Cairo is the language Starknet contracts are written in. It was designed from the start to produce
                programs that can be proven: every operation Cairo performs has to be representable in the
                arithmetic that a STARK proof can efficiently verify. That constraint shapes the language in ways
                that are easy to miss coming from Solidity or Rust.
            </Prose>
            <Prose>
                The most important consequence: Cairo's native value type is not a fixed-width integer. It's a{' '}
                <strong>field element</strong>.
            </Prose>
        </StageSection>
    )),

    content('felt-basics', () => (
        <StageSection title="felt252: arithmetic modulo a large prime">
            <Prose>
                A <code>felt252</code> is an element of a finite field defined by the prime{' '}
                <code>P = 2<sup>251</sup> + 17·2<sup>192</sup> + 1</code>. Every add, subtract, and multiply happens{' '}
                <em>modulo P</em>. There's no concept of overflow the way there is in a 256-bit integer: a result
                that lands outside <code>[0, P)</code> is simply folded back into that range, silently, with no
                revert and no error.
            </Prose>
            <Prose>
                This is different from Solidity's checked arithmetic (which reverts on overflow) and from Rust's
                wrapping/checked/saturating integer methods. felt252 always wraps modulo P, unconditionally; it's
                the language's baseline arithmetic, not an opt-in mode.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('felt-demo', () => (
        <StageSection eyebrow="Try it" title="Interactive felt252 field math">
            <Prose>
                Push the values past the prime and watch the field fold them back in. Try subtracting a small number
                from 0, or multiplying two large values; both land you outside <code>[0, P)</code> before Cairo
                wraps the result.
            </Prose>
            <Box sx={{ mt: 2 }}>
                <FeltArithmeticDemo />
            </Box>
        </StageSection>
    )),

    content('sierra-and-casm', () => (
        <StageSection title="From Cairo, through Sierra, to CASM">
            <Prose>
                Cairo source doesn't compile directly to the bytecode the Starknet OS executes. It goes through an
                intermediate representation called <strong>Sierra</strong> first, which is then compiled to{' '}
                <strong>CASM</strong> (Cairo Assembly), the actual instructions run and proven on-chain.
            </Prose>
            <KeyPoints items={[
                <>Sierra is designed so that every program it represents is provably safe to run: CASM generated from valid Sierra can't get "stuck" in a way that breaks the prover.</>,
                <>This matters for fees: a transaction that reverts still gets included and charged, because Starknet must be able to prove that the revert itself happened correctly.</>,
                <>Declaring a contract publishes its compiled class (identified by a class hash); deploying creates an instance of that class with its own storage.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('why-it-matters', () => (
        <StageSection title="Why this is a security topic, not just trivia">
            <Prose>
                Every assumption you'd bring from a bounded-integer language can be wrong in Cairo. A balance check
                written as if felt252 behaved like a <code>uint256</code> (reverting on underflow, saturating at a
                maximum) will instead wrap silently. An accounting bug that would revert loudly in Solidity can
                complete successfully in Cairo, producing a wrong but "valid" result that the STARK proof will
                faithfully attest to.
            </Prose>
            <Prose>
                Keep the felt252 demo in mind for the rest of this track: Starknet security depends on exact field
                arithmetic, not on the bounded-integer intuition most languages train you to rely on.
            </Prose>
        </StageSection>
    )),

    question(2),
];
